import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const INTL_SOURCE = 'https://tc39.es/ecma402/';
const ECMA_TIMEZONE = 'https://tc39.es/ecma262/#sec-date-timezone-string';
const SCREEN_SOURCE = 'https://www.w3.org/TR/cssom-view/';

/**
 * Timezone / screen / Intl coherence.
 *
 * These are three individually-weak signals that become strong together:
 *  - `Date.getTimezoneOffset()` is a NUMBER derived from the engine's timezone
 *    database; the IANA name from `Intl.DateTimeFormat().resolvedOptions()` is
 *    a STRING from the same database. Spoofing tools usually patch one and not
 *    the other, so they disagree.
 *  - The screen's reported dimensions and the devicePixelRatio should be
 *    plausible for the claimed OS class (a "mobile" UA with a 3840px screen is
 *    inconsistent).
 *  - Intl exposes the resolved locale and numbering system, which a UA-string
 *    spoof rarely touches.
 *
 * We also sample the offset at a mid-year and a mid-year-DST instant, because a
 * faked `getTimezoneOffset` that does not change across a DST boundary is a tell
 * for a zone that does not observe DST.
 */

export const coherenceProbe2: Probe = {
  id: 'coherence2',
  label: 'Timezone / screen / Intl coherence',
  layer: 'locale',
  tier: 'client',
  category: 'browser',
  description: 'Cross-checks offset vs IANA name, DST behaviour, and screen vs device class',
  sources: [INTL_SOURCE, ECMA_TIMEZONE],
  budgetMs: 1500,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];

    // --- 1. offset vs IANA name -------------------------------------------
    const dtf = new Intl.DateTimeFormat();
    const resolved = dtf.resolvedOptions();
    const tzName = resolved.timeZone ?? null;

    const now = new Date();
    const offsetNow = now.getTimezoneOffset();
    const offsetJan = new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
    const offsetJul = new Date(now.getFullYear(), 6, 1).getTimezoneOffset();
    const observesDst = offsetJan !== offsetJul;

    // Does the engine's offset agree with the offset the named zone SHOULD
    // have? We cannot look up the zone database ourselves, but we CAN detect
    // the classic spoof: a patched IANA name with an untouched offset. A
    // strong heuristic is the reverse — compute the offset in a few candidate
    // zones via Intl and see if any matches the reported offset. If the
    // reported name is one of them, the name and offset are at least
    // internally consistent.
    const consistency = checkOffsetAgainstZone(offsetNow, tzName);

    if (consistency === 'mismatch') {
      findings.push({
        id: 'coherence2.tz-offset-mismatch',
        severity: 'medium',
        category: 'browser',
        layer: 'locale',
        confidence: 'medium',
        title: 'Timezone name and UTC offset disagree',
        detail:
          'Intl reports one IANA timezone while Date.getTimezoneOffset() reports an offset that does not correspond to it. Anti-detect tools routinely patch the IANA string without touching the engine offset calculation (or vice versa), which produces exactly this split.',
        sources: [INTL_SOURCE, ECMA_TIMEZONE],
        evidence: [`Intl timeZone=${tzName}`, `getTimezoneOffset=${offsetNow}min`],
        scoreContribution: 20,
      });
    }

    // --- 2. DST coherence ---------------------------------------------------
    // A zone that claims to observe DST but whose offset never changes across
    // the year boundary is suspicious; conversely a fixed zone that changes is
    // impossible.
    if (tzName && observesDst) {
      // fine
    } else if (tzName && !observesDst) {
      // Most real zones (UTC, Asia/Tokyo, etc.) do NOT observe DST, so this is
      // normal; only flag if the zone is one that famously DOES observe DST.
      const dstZones = /^(Europe\/|America\/New_York|America\/Los_Angeles|Australia\/)/;
      if (dstZones.test(tzName)) {
        findings.push({
          id: 'coherence2.dst-missing',
          severity: 'low',
          category: 'browser',
          layer: 'locale',
          confidence: 'low',
          title: `Timezone ${tzName} should observe DST but the offset is constant`,
          detail:
            'The named timezone is in a region that normally observes daylight saving, but the reported offset did not change across the year boundary. This can indicate a frozen or faked offset.',
          sources: [ECMA_TIMEZONE],
          evidence: [`timeZone=${tzName}`, `jan=${offsetJan}`, `jul=${offsetJul}`],
          scoreContribution: 0, // weak; many real zones are fixed
        });
      }
    }

    // --- 3. screen vs device class -----------------------------------------
    const ua = navigator.userAgent;
    const claimsMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    const s = screen;
    const screenPlausibility = classifyScreen(s.width, s.height, claimsMobile);

    if (screenPlausibility === 'implausible') {
      findings.push({
        id: 'coherence2.screen-implausible',
        severity: 'medium',
        category: 'hardware',
        layer: 'display',
        confidence: 'medium',
        title: 'Screen dimensions are implausible for the claimed device class',
        detail:
          'The reported screen size is not consistent with the device class implied by the User-Agent. A mobile UA reporting a large desktop screen (or the reverse) is a common spoofing artefact.',
        sources: [SCREEN_SOURCE],
        evidence: [`screen=${s.width}x${s.height}`, `claimsMobile=${claimsMobile}`],
        scoreContribution: 15,
      });
    }

    // --- 4. Intl resolved details -----------------------------------------
    return {
      signals: {
        available: ok(true, 'client'),
        timeZone: ok(tzName, 'client'),
        offsetNowMinutes: ok(offsetNow, 'client'),
        offsetJanuaryMinutes: ok(offsetJan, 'client'),
        offsetJulyMinutes: ok(offsetJul, 'client'),
        observesDst: ok(observesDst, 'derived'),
        offsetNameConsistency: ok(consistency, 'derived'),
        resolvedLocale: ok(resolved.locale ?? null, 'client'),
        calendar: ok(resolved.calendar ?? null, 'client'),
        numberingSystem: ok(resolved.numberingSystem ?? null, 'client'),
        // Timezone part of the Intl object
        intlSupportedValues: ok(typeof Intl.supportedValuesOf === 'function', 'client'),
        supportedTimeZones: ok(getSupportedTimeZoneSample(), 'client'),
        screenWidth: ok(s.width, 'client'),
        screenHeight: ok(s.height, 'client'),
        screenPlausibility: ok(screenPlausibility, 'derived'),
      },
      findings,
      parts: {
        localeTimezone: {
          timeZone: tzName,
          observesDst,
          resolvedLocale: resolved.locale ?? null,
          numberingSystem: resolved.numberingSystem ?? null,
        },
      },
    };
  },
};

/**
 * Is the numeric offset consistent with the named zone? We probe a set of
 * well-known zones and compare their current offsets with the reported one. If
 * the named zone's own current offset (as computed by Intl formatting a date in
 * that zone) equals the reported offset, they agree.
 */
function checkOffsetAgainstZone(
  reportedOffsetMin: number,
  tzName: string | null,
): 'consistent' | 'mismatch' | 'unknown' {
  if (!tzName) return 'unknown';
  try {
    // Format a date in the claimed zone and read the zone's offset from the
    // formatted parts.
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      timeZoneName: 'longOffset',
    });
    const part = fmt.formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
    if (!part) return 'unknown';
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(part);
    if (!m) return 'unknown'; // "GMT" => offset 0
    const sign = m[1] === '+' ? -1 : 1; // JS offsets are inverted
    const hours = Number(m[2]);
    const mins = m[3] ? Number(m[3]) : 0;
    const zoneOffsetMin = sign * (hours * 60 + mins);
    return zoneOffsetMin === reportedOffsetMin ? 'consistent' : 'mismatch';
  } catch {
    return 'unknown';
  }
}

function classifyScreen(
  w: number,
  h: number,
  claimsMobile: boolean,
): 'plausible' | 'implausible' | 'unknown' {
  if (!w || !h) return 'unknown';
  if (claimsMobile) {
    // Phones are typically <= 560 CSS px wide in portrait.
    return w <= 560 ? 'plausible' : 'implausible';
  }
  // For desktop/tablet, a screen of at least 200x200 is plausible.
  return w >= 200 && h >= 200 ? 'plausible' : 'implausible';
}

function getSupportedTimeZoneSample(): string[] {
  if (typeof Intl.supportedValuesOf !== 'function') return [];
  try {
    const tzs = Intl.supportedValuesOf('timeZone') as string[];
    return [tzs[0], tzs[Math.floor(tzs.length / 2)], tzs[tzs.length - 1]].filter(Boolean);
  } catch {
    return [];
  }
}

registerProbe(coherenceProbe2);
